import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtUsd } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { MapPin, AlertTriangle, Phone, X } from "lucide-react";
import { RiderFlagBanner } from "@/components/RiderFlagBanner";

const POLL_MS = 3000;
const TTL_SECONDS = 45;
// Custom audio file uploaded by user (v2.6) — distinct tone for driver popup
// (loops the driver-notification.mp3 alarm uploaded May 2026).
const DISPATCH_TONE = "/sounds/driver-dispatch-alert.mp3";

// v2.7.65+E221 · The active Driver Dispatch Alert tone is admin-configurable
// via the Audio Test Panel. We resolve it once per session (falling back to
// the default) and every fresh alarm element uses it.
let _dispatchToneUrl = DISPATCH_TONE;
async function refreshDispatchTone() {
  try {
    const { data } = await api.get("/system-sounds/active/driver_dispatch");
    if (data && data.url) _dispatchToneUrl = data.url;
  } catch { /* keep default on any error */ }
}

// AUTOPLAY UNLOCK — v2.6.7 fix for "alarm on app open" bug.
// Prior attempts to prime the REAL alarm file at volume=0/muted=true still
// emitted brief audible blips on certain browsers (iOS Safari especially) and
// would also fire the alarm if a stale offer was found on first poll.
//
// New approach: prime the autoplay policy with a guaranteed-silent inline WAV
// data URI (44 bytes — empty PCM payload). The browser registers a successful
// audio.play() under user gesture, satisfying the autoplay policy for later
// real .play() calls on the actual alarm element. No real-alarm bytes are ever
// decoded during the prime.
const SILENT_WAV_DATA_URI =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";

let _audioUnlocked = false;
function primeAutoplay() {
  if (_audioUnlocked) return;
  try {
    const el = new Audio(SILENT_WAV_DATA_URI);
    el.volume = 0.0;
    el.muted = true;
    const p = el.play();
    if (p && p.then) {
      p.then(() => { try { el.pause(); } catch {} _audioUnlocked = true; })
       .catch(() => { /* still blocked — will retry on next gesture */ });
    } else {
      _audioUnlocked = true;
    }
  } catch { /* noop */ }
}
if (typeof window !== "undefined") {
  const unlock = () => { primeAutoplay(); };
  const opts = { once: true, capture: true };
  window.addEventListener("pointerdown", unlock, opts);
  window.addEventListener("keydown", unlock, opts);
  window.addEventListener("touchstart", unlock, opts);
}

function makeDispatchAudio() {
  // Create a fresh Audio element for each new alarm. Once autoplay was
  // primed via the silent data URI, .play() on a new HTMLAudioElement is
  // allowed by Chrome / Firefox / Edge / Safari for the session.
  try {
    const el = new Audio(_dispatchToneUrl || DISPATCH_TONE);
    el.loop = true;
    el.volume = 1.0;
    el.preload = "auto";
    return el;
  } catch { return null; }
}

function CountdownBar({ secondsLeft, total }) {
  const pct = Math.max(0, Math.min(100, (secondsLeft / total) * 100));
  const tint = secondsLeft <= 5 ? "bg-red-500" : secondsLeft <= 12 ? "bg-amber-400" : "bg-green-400";
  return (
    <div className="relative h-1.5 bg-zinc-800 rounded-full overflow-hidden mt-2 mb-3" data-testid="countdown-bar">
      <div className={`absolute inset-y-0 left-0 ${tint} transition-all duration-500 ease-linear`} style={{ width: `${pct}%` }}/>
    </div>
  );
}

function SlideToAccept({ onAccept, disabled }) {
  const [x, setX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef(null);
  const widthRef = useRef(0);

  useEffect(() => {
    const w = trackRef.current?.clientWidth || 0;
    widthRef.current = Math.max(0, w - 48);
  });

  const onStart = () => !disabled && setDragging(true);
  const onMove = (e) => {
    if (!dragging) return;
    const rect = trackRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const nx = Math.max(0, Math.min(widthRef.current, cx - rect.left - 24));
    setX(nx);
  };
  const onEnd = () => {
    if (!dragging) return;
    setDragging(false);
    if (x >= widthRef.current * 0.85) {
      onAccept?.();
    } else {
      setX(0);
    }
  };

  return (
    <div
      ref={trackRef}
      onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
      onTouchMove={onMove} onTouchEnd={onEnd}
      className={`relative h-14 rounded-full overflow-hidden border-2 ${disabled ? "bg-zinc-800 border-zinc-700" : "bg-green-500/20 border-green-400"} select-none`}
      data-testid="slide-to-accept-track"
    >
      <div className="absolute inset-0 flex items-center justify-center text-sm uppercase tracking-widest font-bold text-green-300/80 pointer-events-none">
        SLIDE TO ACCEPT →
      </div>
      <div
        onMouseDown={onStart} onTouchStart={onStart}
        className={`absolute top-1 left-1 h-12 w-12 rounded-full bg-green-500 hover:bg-green-400 cursor-grab active:cursor-grabbing flex items-center justify-center text-white font-extrabold ${disabled ? "opacity-50" : ""}`}
        style={{ transform: `translateX(${x}px)`, transition: dragging ? "none" : "transform 0.25s ease" }}
        data-testid="slide-to-accept-handle"
      >
        →
      </div>
    </div>
  );
}

const STATUS_LABEL = {
  intoxicated: "INTOXICATED — APPROACH WITH CARE",
  combative: "COMBATIVE — POSSIBLE AGGRESSION",
  dui_pickup: "DUI PICKUP",
  law_enforcement_dui: "LAW ENFORCEMENT DUI PICKUP",
};

export default function DriverDispatchModal() {
  const { driver } = useAuth();
  const [offer, setOffer] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(TTL_SECONDS);
  const [busy, setBusy] = useState(false);
  const lastOfferIdRef = useRef(null);
  const audioRef = useRef(null);

  const stopTone = () => {
    const a = audioRef.current;
    if (a) { try { a.pause(); a.currentTime = 0; } catch {} }
  };
  const startTone = () => {
    if (!audioRef.current) audioRef.current = makeDispatchAudio();
    const a = audioRef.current;
    if (!a) return;
    try { a.currentTime = 0; a.volume = 1.0; a.muted = false; const p = a.play(); if (p && p.catch) p.catch(()=>{}); } catch {}
  };
  // Stop tone when component unmounts
  useEffect(() => () => stopTone(), []);

  useEffect(() => {
    if (!driver) return;
    let cancelled = false;
    // v2.7.65+E221 · Resolve the admin-configured Driver Dispatch Alert tone.
    refreshDispatchTone();
    const tick = async () => {
      try {
        const { data } = await api.get("/drivers/offers");
        if (cancelled) return;
        if (data.offer && data.offer.id !== lastOfferIdRef.current) {
          // v2.6.9 batch BO · ALARM FIX
          // Previous gate (`firstPollRef`) silenced the alarm on the very
          // first poll to stop stale offers from ringing at app-open. Side
          // effect: a FRESH offer arriving 1-2 s before the first poll also
          // got silenced — drivers were missing bookings.
          //
          // New gate: ring iff the offer was created within ~25 s. Stale
          // offers (older than the TTL) are skipped silently; everything
          // fresh fires the alarm + visual together.
          lastOfferIdRef.current = data.offer.id;
          setOffer(data.offer);
          const expIso = data.offer.expires_at;
          const sec = Math.max(0, Math.round((new Date(expIso).getTime() - Date.now()) / 1000));
          setSecondsLeft(sec);
          const createdAt = data.offer.created_at ? new Date(data.offer.created_at).getTime() : Date.now();
          const ageSec = Math.max(0, (Date.now() - createdAt) / 1000);
          const isFresh = ageSec < 25; // < TTL_SECONDS (45) with safety margin
          if (isFresh) startTone();
        }
      } catch { /* poll silently */ }
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    // v2.7.65+E221 · When the driver push SW receives a `ride_offer`, poll
    // immediately so a foreground/visible app rings the instant dispatch
    // pushes — no waiting up to 3s for the next poll.
    const onSwMessage = (e) => {
      if (e?.data?.kind === "ride_offer") { refreshDispatchTone(); tick(); }
    };
    try { navigator.serviceWorker?.addEventListener("message", onSwMessage); } catch { /* noop */ }
    return () => {
      cancelled = true;
      clearInterval(t);
      try { navigator.serviceWorker?.removeEventListener("message", onSwMessage); } catch { /* noop */ }
    };
  }, [driver]);

  useEffect(() => {
    if (!offer) { stopTone(); return; }
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        const next = s - 1;
        if (next <= 0) {
          stopTone();
          setOffer(null);
          lastOfferIdRef.current = null;
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [offer]);

  const accept = async () => {
    if (!offer || busy) return;
    setBusy(true); stopTone();
    try {
      const { data } = await api.post(`/drivers/offers/${offer.id}/accept`);
      toast.success(`Ride accepted · ${data.ride.waybill_no}`);
      setOffer(null);
      window.dispatchEvent(new CustomEvent("ride-accepted", { detail: data.ride }));
    } catch (e) {
      const code = e?.response?.status;
      if (code === 409) toast.error("Ride already taken by another driver");
      else if (code === 410) toast.error("Offer expired");
      else toast.error(e?.response?.data?.detail || "Could not accept");
      setOffer(null);
    } finally { setBusy(false); }
  };

  const decline = async () => {
    if (!offer || busy) return;
    setBusy(true); stopTone();
    try { await api.post(`/drivers/offers/${offer.id}/decline`); toast.message("Ride declined"); }
    catch { /* ignore */ }
    finally { setBusy(false); setOffer(null); }
  };

  if (!offer) return null;
  const cs = offer.client_status && offer.client_status !== "normal" ? offer.client_status : null;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) decline(); }}>
      <DialogContent className="bg-zinc-900 border-amber-400 text-zinc-100 max-w-md sm:max-w-lg" data-testid="dispatch-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"/>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-400"/>
            </span>
            NEW {offer.scheduled ? "RESERVED" : ""} RIDE OFFER
          </DialogTitle>
        </DialogHeader>

        <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex justify-between">
          <span>Auto-decline in</span>
          <span className="meter-display text-amber-400 font-bold" data-testid="seconds-left">{secondsLeft}s</span>
        </div>
        <CountdownBar secondsLeft={secondsLeft} total={TTL_SECONDS}/>

        {/* v2.6.9 batch Y · rider flag badge surfaces BEFORE the accept slider */}
        {offer.rider_flag && <RiderFlagBanner flag={offer.rider_flag} data-testid="dispatch-rider-flag"/>}

        <div className="bg-zinc-950 border border-zinc-800 rounded p-4 space-y-2 text-sm">
          <div className="flex items-center justify-between gap-2">
            <Badge variant="outline" className={offer.kind === "business" ? "border-blue-400/40 text-blue-300" : "border-zinc-700"}>{offer.kind === "business" ? "BUSINESS" : "RIDER"}</Badge>
            {cs && <Badge variant="outline" className="border-red-500/60 text-red-400 text-[10px] uppercase animate-pulse"><AlertTriangle className="h-3 w-3 mr-1"/>{STATUS_LABEL[cs] || cs}</Badge>}
          </div>
          <div className="flex justify-between"><span className="text-zinc-400">From</span><span className="font-semibold truncate ml-2 max-w-xs">{offer.origin}</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">To</span><span className="font-semibold truncate ml-2 max-w-xs">{offer.destination}</span></div>
          {offer.stops?.length > 0 && <div className="flex justify-between"><span className="text-zinc-400">Stops</span><span className="font-semibold">{offer.stops.length}</span></div>}
          <div className="flex justify-between"><span className="text-zinc-400">Pax</span><span className="meter-display">{offer.passengers}</span></div>
          {offer.business_name && <div className="flex justify-between"><span className="text-zinc-400">Business</span><span className="font-semibold truncate ml-2">{offer.business_name}</span></div>}
          {offer.reservation_name && <div className="flex justify-between"><span className="text-zinc-400">Reserved by</span><span className="font-semibold">{offer.reservation_name}</span></div>}
          {offer.customer_name && !offer.business_name && <div className="flex justify-between"><span className="text-zinc-400">Rider</span><span className="font-semibold">{offer.customer_name}</span></div>}
          <div className="flex justify-between border-t border-zinc-800 pt-2"><span className="text-zinc-400">Payment</span><span className="uppercase font-bold">{offer.payment_method}</span></div>
          {offer.tip_amount > 0 && <div className="flex justify-between"><span className="text-zinc-400">Tip included</span><span className="text-green-400 meter-display">{fmtUsd(offer.tip_amount)}</span></div>}
          {offer.payment_status === "captured" && <div className="flex justify-between"><span className="text-zinc-400">Card</span><span className="text-green-400 font-bold">CHARGED ✓</span></div>}
          {offer.payment_status === "authorized" && <div className="flex justify-between"><span className="text-zinc-400">Card</span><span className="text-green-400 font-bold">HELD ✓</span></div>}
          <div className="flex justify-between border-t border-zinc-800 pt-2 mt-1"><span className="text-zinc-300 font-semibold">FARE</span><span className="meter-display text-2xl font-extrabold text-amber-400" data-testid="offer-fare">{fmtUsd(offer.total)}</span></div>
        </div>

        {/* v2.6.9 batch Z · BLOCK-severity riders cannot be accepted. Replace the slider
            with a disabled red banner. Decline is still allowed so the offer clears. */}
        {(offer.rider_flag?.severity || "").toLowerCase() === "block" ? (
          <div className="w-full bg-red-500/15 border-2 border-red-500/60 rounded p-4 text-center" data-testid="dispatch-block-notice">
            <div className="font-extrabold text-red-200 text-sm uppercase tracking-wider mb-1">🚫 RIDER BLOCKED</div>
            <div className="text-[11px] text-red-200/90">You cannot accept this ride. Decline to clear the offer and contact dispatch.</div>
          </div>
        ) : (
          <SlideToAccept onAccept={accept} disabled={busy}/>
        )}
        <Button data-testid="dispatch-decline-btn" onClick={decline} disabled={busy} variant="outline" className="w-full bg-zinc-950 border-red-500/40 text-red-300 hover:bg-red-500/10">
          <X className="h-4 w-4 mr-1"/>DECLINE
        </Button>
      </DialogContent>
    </Dialog>
  );
}
